import { useEffect, useMemo, useRef, useState } from "react";
import { Check, PenLine, Pencil, Plus, Tag, X } from "lucide-react";
import { ComposeUnit } from "@/components/ComposeUnit";
import { WritingEditor, plainTextOf, sanitizeFormatting } from "@/components/WritingEditor";

const AUTOSAVE_MS = 1200;
import { otNoPaste, otOwnCopy } from "@/lib/otPasteGuard";
import { findSpan, paragraphLike, splitSentences } from "@/lib/unitText";
import { annotateFormatting } from "@/lib/formatAnnotate";
import { clearDraft, draftChanged, getDraft, setDraft } from "@/lib/writingDraft";

/*
  UDWW 3C-4b — ESSAY VIEW. The student's living essay read as ordinary writing, in the ONE student order. It is a
  PROJECTION of the same authoritative units the Structure view shows (`document` = the backend's `_document_view(ot)`):
  no second draft, no second text authority. Editing a paragraph here is the SAME patch the map makes on the same unit.

  ESSAY-AS-WORKSPACE (frame="writing", the consolidated teaching loop): THE ESSAY ITSELF IS THE WORKSPACE. Coherent prose,
  not cards: every unit of the student's writing is a paragraph of the essay (nothing is "unplaced"); no provisional badges,
  dashed boundaries, shading, numbering or TWS-authored descriptions; the thesis is a MARKED SENTENCE INSIDE its paragraph
  (material before and after it), movable and revisable there — never extracted above the essay; regions and working titles
  appear only once the controller has licensed structure. TWS may think complexly; the student works simply.
*/
const REGION_TITLE = { introduction: "Introduction", body: "Body", conclusion: "Conclusion" };
const openingWords = (t, n = 8) => { const w = String(t || "").trim().split(/\s+/).filter(Boolean); return w.length <= n ? w.join(" ") : `${w.slice(0, n).join(" ")}\u2026`; };   // whole words only, never mid-word
// TWO DIFFERENT ACTS, NAMED BY WHAT THE STUDENT DOES: EDIT TEXT = change the wording yourself, in place; WORK WITH COACH = this part's coached writing state
export const EDIT_TEXT_TIP = "Change the wording yourself.";
export const WORK_WITH_COACH_TIP = "Get help developing or improving this part.";

export function EssayView({
  doc, items = [], onSaveThesis, onSaveIdea, onTitle,
  compose = {}, composeReady = {}, composeFocus = null, composeView = null, composeBusy = false,
  onComposeOpen, onComposeReturn, onComposeTurn,
  frame = "essay", structureLicensed = false, pendingThesis = "", onAddParagraph, onMarkThesis, onClearThesis,
  refineOpen = false,   // DEVELOPMENTAL SUFFICIENCY: the student chose to move toward paragraph refinement — every paragraph offers "Work on this part"; THEY pick which
  draftScope = "",   // WRITING ENTRY: unsaved text is kept (lib/writingDraft) per session across record refreshes and view switches
  onSetRegionStart,  // GLOBAL STRUCTURE OWNERSHIP: (id, region) — "Introduction / Body / Conclusion starts here", offered once structure is licensed
  coverage = null, onCoverage,   // the EXISTING global-coverage checkpoint, presented in Essay view when due ({due, status, stale, question})
  canUndo = false, onUndo,
}) {
  const writing = frame === "writing";
  const [regionPick, setRegionPick] = useState(null);   // block id whose "Starts a new part…" choices are open
  const [editing, setEditing] = useState(null);        // { id, text, initial, opened }  (id "thesis" for the thesis)
  const [titling, setTitling] = useState(null);        // { id, text }
  const [throughline, setThroughline] = useState(false);
  const [thesisMenu, setThesisMenu] = useState(false); // the small menu on the marked main-idea sentence
  const [picking, setPicking] = useState(null);        // block id whose sentences are offered as the main idea
  const [adding, setAdding] = useState(false);
  const [addText, setAddText] = useState("");
  const blocks = useMemo(() => (doc && doc.blocks) || [], [doc]);
  const thesis = ((doc && doc.thesis && doc.thesis.text) || "").trim();
  const locus = (doc && doc.thesis && doc.thesis.locus) || null;
  const thesisInPlace = writing && !!thesis && !!locus;
  // THE THESIS LABEL — existing state only (ot.thesis.status projected on doc.thesis.status): an established / confirmed thesis reads "Thesis";
  // anything else the student has marked (provisional, developing, unknown) reads "Provisional thesis". No inference here.
  const thesisStatus = ((doc && doc.thesis && doc.thesis.status) || "").toLowerCase();
  // PROPOSAL vs OWNERSHIP: `doc.thesis.student_judged === false` = TWS recognized the student's sentence as the main idea and the student has not
  // judged it → tentative label + "Yes, this is my main idea"; true or absent (legacy) → the ordinary thesis / provisional-thesis language
  const thesisRecognized = doc?.thesis?.student_judged === false;
  const thesisLabel = thesisRecognized ? "TWS reads this as your main idea" : ["established", "confirmed", "final"].includes(thesisStatus) ? "Thesis" : "Provisional thesis";
  const provisionalThesis = !writing && !thesis ? ((doc && doc.provisional_thesis) || "").trim() : "";   // ROUTING (old frame): TWS's reading, student's words
  const itemById = (id) => items.find((i) => i.id === id);
  const docUnits = doc && doc.units;
  // THE DRAFT FOLLOWS THE UNIT: opening an editor records what it showed; typing updates the draft; only Return / Save / Cancel end it.
  const dGet = (id) => getDraft(draftScope, id);
  const openEdit = (id, text, initial) => {
    const fmt = id === "thesis" ? "" : ((itemById(id) || {}).formatting || "");
    setDraft(draftScope, id, text, text); setEditing({ id, text, initial, opened: text, formatting: fmt, openedFormatting: fmt, autosaved: false, saveState: "" });
  };
  const editingRef = useRef(null); editingRef.current = editing;
  const autosaveTimer = useRef(null);
  // AUTOSAVE (quiet — the SAME patch call the Save button uses; never a coaching turn): a meaningful change persists after a short pause or on blur
  const autosaveNow = async () => {
    const e = editingRef.current;
    if (!e || e.id === "thesis" || !e.text.trim() || (e.text.trim() === (e.initial || "").trim() && (e.formatting || "") === (e.openedFormatting || ""))) return;
    setEditing((x) => (x && x.id === e.id ? { ...x, saveState: "saving" } : x));
    try {
      await saveIdeaWithFormatting(e.id, e.text.trim(), e.formatting, e.openedFormatting);
      setEditing((x) => (x && x.id === e.id ? { ...x, autosaved: true, saveState: "saved" } : x));
      setTimeout(() => setEditing((x) => (x && x.id === e.id && x.saveState === "saved" ? { ...x, saveState: "" } : x)), 1800);
    } catch { setEditing((x) => (x && x.id === e.id ? { ...x, saveState: "" } : x)); }
  };
  const scheduleAutosave = () => { clearTimeout(autosaveTimer.current); autosaveTimer.current = setTimeout(autosaveNow, AUTOSAVE_MS); };
  useEffect(() => () => clearTimeout(autosaveTimer.current), []);
  const changeEdit = ({ text, formatting }) => {
    setDraft(draftScope, editing.id, text, editing.opened);            // every change enters the recoverable draft store immediately
    setEditing({ ...editing, text, formatting: editing.id === "thesis" ? "" : formatting, saveState: "editing" });
    if (editing.id !== "thesis") scheduleAutosave();
  };
  // CANCEL = intentionally discard THIS editing episode: the draft is dropped and, if autosave already persisted, the editor-open text AND
  // formatting are restored through the same save call — Cancel never pretends to discard what stays persisted.
  const cancelEdit = async () => {
    if (!editing) return;
    clearTimeout(autosaveTimer.current);
    const { id, opened, openedFormatting, autosaved } = editing;
    clearDraft(draftScope, id);
    setEditing(null);
    if (autosaved && id !== "thesis" && (opened || "").trim()) { try { await saveIdeaWithFormatting(id, opened.trim(), openedFormatting, "x"); } catch { /* the record shows what stands */ } }
  };
  // plain writing saves exactly as before (id, text); formatting metadata rides along only when there is (or was) any
  const saveIdeaWithFormatting = (id, text, formatting, openedFormatting) =>
    (formatting || openedFormatting) ? onSaveIdea(id, text, formatting || "") : onSaveIdea(id, text);
  // A RECORD REFRESH (a coach turn, a save elsewhere) never wipes an unsaved draft: the editor stays open on it; an unchanged editor closes as before.
  useEffect(() => {
    setTitling(null); setPicking(null); setThesisMenu(false);
    setEditing((e) => (e && draftChanged(dGet(e.id)) && (e.id === "thesis" || blocks.some((b) => b.id === e.id)) ? e : null));
  }, [docUnits, blocks.length]);   // eslint-disable-line react-hooks/exhaustive-deps
  // RETURNING TO THE VIEW: a draft left behind (view switch, reload) reopens where it was.
  useEffect(() => {
    if (editing || adding) return;
    for (const b of blocks) {
      const d = dGet(b.id);
      if (draftChanged(d) && itemById(b.id)) { setEditing({ id: b.id, text: d.text, initial: b.text, opened: d.opened, formatting: "", openedFormatting: (itemById(b.id) || {}).formatting || "", autosaved: false, saveState: "editing" }); return; }
    }
    const dt = dGet("thesis");
    if (thesis && draftChanged(dt)) { setEditing({ id: "thesis", text: dt.text, initial: thesis, opened: dt.opened }); return; }
    const dn = dGet("new");
    if (onAddParagraph && draftChanged(dn)) { setAdding(true); setAddText(dn.text); }
  }, [blocks, thesis]);   // eslint-disable-line react-hooks/exhaustive-deps

  // a root's subtree = itself + the blocks that follow it with a greater depth (the projection is a DFS in student order)
  const groups = useMemo(() => {
    const out = [];
    blocks.forEach((b, k) => {
      if (b.depth !== 0) return;
      const kids = [];
      for (let j = k + 1; j < blocks.length && blocks[j].depth > 0; j++) kids.push(blocks[j]);
      out.push({ root: b, kids });
    });
    return out;
  }, [blocks]);
  // WRITING FRAME: every unit of the student's WRITING is the essay — except a candidate part they generated in dialogue and have not placed
  // (a thinking object: shown under "Thoughts I'm still developing", never as essay prose until placed)
  const placed = writing ? groups.filter((g) => !g.root.candidate) : groups.filter((g) => g.root.established || g.root.kind === "group");
  const developing = writing ? groups.filter((g) => !!g.root.candidate) : groups.filter((g) => !(g.root.established || g.root.kind === "group"));
  const regionsActive = !!(doc && doc.regions_active);

  // ONE SAVE: Return, the Save button and the commit before a TWS action all end here — the same call the record already has.
  const saveEdit = async () => {
    if (!editing) return;
    clearTimeout(autosaveTimer.current);
    const { id, text, initial, opened, formatting, openedFormatting } = editing;
    const t = text.trim();
    clearDraft(draftScope, id);                                   // cleared first: nothing commits twice
    try {
      if (t && (t !== (initial || "").trim() || (formatting || "") !== (openedFormatting || ""))) {
        if (id === "thesis") await onSaveThesis(t);
        else await saveIdeaWithFormatting(id, t, formatting, openedFormatting);   // SAVE NOW: the same call autosave uses
      }
      setEditing(null);
    } catch {
      setDraft(draftScope, id, text, opened);                     // the save failed: the words stay in the editor (the open editor is the signal)
    }
  };
  const saveTitle = async () => {
    if (!titling) return;
    const t = titling.text.trim();
    const it = itemById(titling.id);
    if (t && it && t !== (it.label || "").trim()) await onTitle(it, t);
    setTitling(null);
  };
  // THE NEXT PARAGRAPH: every change enters the recoverable draft store (survives view switches and reloads); Add creates the paragraph
  // (the existing add + coaching route) — autosave never creates paragraphs or coaching turns on its own.
  const [addFormatting, setAddFormatting] = useState("");
  const changeAdd = ({ text, formatting }) => { setAddText(text); setAddFormatting(formatting || ""); setDraft(draftScope, "new", text, ""); };
  const cancelAdd = () => { clearDraft(draftScope, "new"); setAdding(false); setAddText(""); setAddFormatting(""); };
  const saveAdd = async () => {
    const t = addText.trim();
    if (!t || !onAddParagraph) return;
    const fmt = addFormatting;
    clearDraft(draftScope, "new"); setAdding(false); setAddText(""); setAddFormatting("");
    try { await (fmt ? onAddParagraph(t, fmt) : onAddParagraph(t)); } catch { setDraft(draftScope, "new", t, ""); setAdding(true); setAddText(t); setAddFormatting(fmt); }
  };
  const saveHint = (state) => ({ editing: "Editing\u2026", saving: "Saving\u2026", saved: "Saved" }[state] || "Saves as you write \u00b7 \u2318/Ctrl+Return saves now");

  const editor = (id, initial) => (
    <div data-testid={`essay-edit-${id}`} className="mt-1">
      <WritingEditor testid={`essay-edit-input-${id}`} initialText={editing.text} initialFormatting={editing.formatting}
        onChange={changeEdit} onSave={saveEdit} onCancel={cancelEdit} onBlur={() => { if (id !== "thesis") { clearTimeout(autosaveTimer.current); autosaveNow(); } }} />
      <div className="flex items-center justify-end gap-2 mt-1.5">
        <span data-testid={`essay-edit-hint-${id}`} data-save-state={editing.saveState || ""} className="mr-auto text-[10.5px] text-stone-400">{id === "thesis" ? "\u2318/Ctrl+Return saves" : saveHint(editing.saveState)}</span>
        <button data-testid={`essay-edit-cancel-${id}`} onClick={cancelEdit} className="text-[11.5px] text-stone-500 px-2">Cancel</button>
        <button data-testid={`essay-edit-save-${id}`} onClick={saveEdit} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11.5px] px-3 py-1 rounded"><Check className="h-3 w-3" /> Save now</button>
      </div>
    </div>
  );

  // THE THESIS IN PLACE — the accepted main idea (backend offsets) or the candidate TWS is asking about (located here) is a
  // marked span of the sentence where it stands; nothing is duplicated above the essay. Only the accepted mark is interactive.
  const hasMarks = (b) => !!(writing && ((thesisInPlace && b.thesis) || (!thesis && pendingThesis && locus === null)));
  const markedText = (b, t, k) => {
    if (!writing) return t;
    const where = k === 0 ? "text" : `development:${k - 1}`;
    let span = null, pending = false;
    if (thesisInPlace && b.thesis && b.thesis.where === where) span = { start: b.thesis.start, end: b.thesis.end };
    else if (!thesis && pendingThesis && locus === null) { const s = findSpan(t, pendingThesis); if (s) { span = s; pending = true; } }
    if (!span) return t;
    return (<>
      {t.slice(0, span.start)}
      <span data-testid={pending ? undefined : "essay-thesis-label"} data-label={pending ? undefined : thesisLabel}>
        {/* THESIS LABEL — the quiet label sits IMMEDIATELY BEFORE the thesis span, inside the prose (a real inline element, not a pseudo-element),
            so it labels the underlined sentence itself — never the paragraph that happens to contain it; the underline marks the span in place */}
        {!pending ? (
          <span data-testid="essay-thesis-label-inline" data-thesis-status={thesisStatus || undefined} data-owner={thesisRecognized ? "tws" : "student"} aria-label={thesisLabel}
            className={thesisRecognized
              ? "mr-1.5 inline-block align-[2px] px-0.5 text-[10px] italic leading-none text-stone-400 select-none"
              : "mr-1.5 inline-block align-[2px] rounded-[3px] bg-[#8C3A2A]/[0.07] px-1 py-[1px] font-mono text-[8.5px] uppercase tracking-[0.16em] leading-none text-[#8C3A2A] select-none"}>
            {thesisLabel}
          </span>
        ) : null}
      <span data-testid={pending ? "essay-thesis-candidate" : "essay-thesis-mark"} role={pending ? undefined : "button"} tabIndex={pending ? undefined : 0}
        onClick={(e) => { e.stopPropagation(); if (!pending) setThesisMenu((m) => !m); }}
        onKeyDown={(e) => { if (!pending && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); e.stopPropagation(); setThesisMenu((m) => !m); } }}
        title={pending ? "The Writing Studio is asking whether this is your main idea — answer in the conversation"
          : thesisRecognized ? "The Writing Studio reads this as your main idea — tap to say yes, change its wording, or say it is not your main idea"
          : "Your provisional thesis — tap to change its wording or to say it is not your main idea"}
        className={pending
          ? "underline decoration-dotted decoration-[#8C3A2A]/50 underline-offset-[5px]"
          : thesisRecognized ? "underline decoration-dashed decoration-[#8C3A2A]/45 underline-offset-[5px] cursor-pointer hover:decoration-[#8C3A2A]"
          : "underline decoration-[#8C3A2A]/60 decoration-2 underline-offset-[5px] cursor-pointer hover:decoration-[#8C3A2A]"}>{t.slice(span.start, span.end)}</span>
      </span>
      {t.slice(span.end)}
    </>);
  };

  // FORMATTED PROSE + TWS ANNOTATION — two layers, one rendering: the student's formatting (valid only when it re-renders the canonical text
  // exactly) is the prose; the thesis span (backend offsets in the canonical text, or `findSpan` for a pending candidate) is wrapped INTO
  // that rendered HTML text-node by text-node (lib/formatAnnotate) so nothing about the student's bold / italic / underline / lists changes.
  // The result is derived display state only — `item.formatting` is never touched. If no SAFE inline match exists, the formatting still
  // renders and the thesis status falls back to a separate quiet label under the paragraph (never a guessed highlight).
  const validFormatting = (b) => { const f = ((itemById(b.id) || {}).formatting || ""); return f && plainTextOf(f) === (b.text || "").trim() ? f : ""; };
  const formattedHead = (b) => {
    const fmt = validFormatting(b);
    if (!fmt) return null;
    const clean = sanitizeFormatting(fmt);
    if (!hasMarks(b)) return { html: clean, marked: false, unmatched: false };
    let span = null, pending = false;
    if (thesisInPlace && b.thesis && b.thesis.where === "text") span = { start: b.thesis.start, end: b.thesis.end };
    else if (!thesis && pendingThesis && locus === null) { const s = findSpan(b.text || "", pendingThesis); if (s) { span = s; pending = true; } }
    if (!span) return { html: clean, marked: false, unmatched: false };
    const annotated = annotateFormatting(clean, b.text || "", span, {
      pending, label: thesisLabel, labelId: `tws-thesis-label-${b.id}`, owner: thesisRecognized ? "tws" : "student", status: thesisStatus,
      title: thesisRecognized ? "The Writing Studio reads this as your main idea — tap to say yes, change its wording, or say it is not your main idea"
        : "Your provisional thesis — tap to change its wording or to say it is not your main idea",
    });
    return { html: annotated || clean, marked: !!annotated, unmatched: !annotated && !pending };
  };
  // the fragments live inside dangerouslySetInnerHTML, so the thesis menu opens through delegation (click / Enter / Space on a mark fragment)
  const onMarkEvent = (e) => {
    const m = e.target && e.target.closest ? e.target.closest('[data-tws-thesis="mark"]') : null;
    if (!m || m.getAttribute("data-pending") === "true") return;
    if (e.type === "keydown" && e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault(); e.stopPropagation();
    setThesisMenu((v) => !v);
  };
  const thesisStatusEl = (b, fh) => (fh && fh.unmatched ? (
    <button type="button" data-testid={`essay-thesis-status-${b.id}`} data-owner={thesisRecognized ? "tws" : "student"} data-thesis-status={thesisStatus || undefined}
      onClick={(e) => { e.stopPropagation(); setThesisMenu((v) => !v); }}
      title="Your main idea is in this paragraph — tap to work on it"
      className={thesisRecognized ? "mt-1 text-[10.5px] italic text-stone-400 hover:text-stone-600"
        : "mt-1 rounded-[3px] bg-[#8C3A2A]/[0.07] px-1 py-[1px] font-mono text-[8.5px] uppercase tracking-[0.16em] text-[#8C3A2A] hover:bg-[#8C3A2A]/[0.12]"}>
      {thesisLabel} — in this paragraph
    </button>
  ) : null);

  const thesisMenuEl = (b) => (thesisMenu && thesisInPlace && locus.unit_id === b.id ? (
    <div data-testid="essay-thesis-menu" className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]" onClick={(e) => e.stopPropagation()}>
      <span className={thesisRecognized ? "text-[10.5px] italic text-stone-400" : "font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C3A2A]"}>{thesisRecognized ? thesisLabel : thesisLabel.toLowerCase()}</span>
      {thesisRecognized && onMarkThesis ? (
        <button data-testid="essay-thesis-own" onClick={() => { setThesisMenu(false); onMarkThesis(thesis); }}
          className="text-[#8C3A2A] hover:text-[#6b2a1e] underline underline-offset-2 decoration-[#8C3A2A]/40">Yes, this is my main idea</button>
      ) : null}
      <button data-testid="essay-thesis-reword" onClick={() => { setThesisMenu(false); openEdit("thesis", thesis, thesis); }}
        className="text-stone-600 hover:text-[#8C3A2A] underline underline-offset-2 decoration-stone-300">Change the wording</button>
      {onClearThesis ? (
        <button data-testid="essay-thesis-unmark" onClick={() => { setThesisMenu(false); onClearThesis(); }}
          className="text-stone-600 hover:text-[#8C3A2A] underline underline-offset-2 decoration-stone-300">Not my main idea</button>
      ) : null}
      <button onClick={() => setThesisMenu(false)} className="text-stone-400 hover:text-stone-600" aria-label="close"><X className="h-3 w-3" /></button>
    </div>
  ) : null);

  // "SET AS START OF…" — the smallest structural assertion: this paragraph begins the Introduction / Body / Conclusion. ONE call; the
  // spans follow from document order on the backend (the same canonical `region` state Structure view moves). Never a coaching turn.
  const regionPickEl = (b) => (regionPick === b.id && onSetRegionStart ? (
    <div data-testid={`essay-region-start-${b.id}`} className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px]" onClick={(e) => e.stopPropagation()}>
      <span className="text-stone-500">This paragraph begins the</span>
      {["introduction", "body", "conclusion"].map((rk) => (
        <button key={rk} data-testid={`essay-region-start-${b.id}-${rk}`} onClick={async () => { setRegionPick(null); await onSetRegionStart(b.id, rk); }}
          className={`rounded-full border px-2.5 py-0.5 ${b.region === rk ? "border-[#8C3A2A]/50 text-[#8C3A2A] bg-[#8C3A2A]/[0.05]" : "border-stone-300 text-stone-700 hover:border-[#8C3A2A]/60 hover:text-[#8C3A2A]"}`}>{REGION_TITLE[rk]}</button>
      ))}
      <button data-testid={`essay-region-start-cancel-${b.id}`} onClick={() => setRegionPick(null)} className="text-stone-400 hover:text-stone-600 px-1">Cancel</button>
    </div>
  ) : null);

  const pickerEl = (b, text) => (picking === b.id ? (
    <div data-testid={`essay-pick-thesis-${b.id}`} className="mt-1.5 space-y-0.5" onClick={(e) => e.stopPropagation()}>
      <p className="text-[11.5px] text-stone-500">Which sentence carries your main idea?</p>
      {splitSentences(text.join(" ")).map((s, k) => (
        <button key={k} data-testid={`essay-pick-sentence-${b.id}-${k}`} onClick={() => { setPicking(null); onMarkThesis && onMarkThesis(s); }}
          className="block w-full text-left text-[13px] leading-snug text-stone-800 rounded px-2 py-1 hover:bg-[#8C3A2A]/[0.06]">{s}</button>
      ))}
      <button onClick={() => setPicking(null)} className="text-[11px] text-stone-400 hover:text-stone-600 px-2">cancel</button>
    </div>
  ) : null);

  // WHO ESTABLISHED THE REGION — three states kept distinct (never collapsed): the STUDENT placed it ("student"), TWS proposed it and the
  // student has not yet owned it ("tws"), or nothing is established yet ("none" — shown in the Body's place for document order only).
  const regionOwner = (b) => (REGION_TITLE[b.region] ? (b.provisional_placement ? "tws" : "student") : "none");

  const paragraph = (b, kids, n) => {
    const it = itemById(b.id);
    const provisional = !writing && (b.provenance === "tws_provisional" || b.provenance === "tws_inferred" || b.provisional_placement);
    const isEditing = editing && editing.id === b.id;
    const isTitling = titling && titling.id === b.id;
    const cEntry = compose && compose[b.id];
    const cReady = !!(composeReady && composeReady[b.id] && composeReady[b.id].ready);
    // WRITING FRAME: refining a paragraph is a teaching move — offered once the controller has opened it (the compose entry exists) OR once the
    // STUDENT chose to move toward refining the writing (refineOpen): then every part offers "Work on this part" and the student picks which (never TWS)
    const canRefine = !!onComposeOpen && it && !provisional && b.depth === 0 && b.kind !== "group" && (writing ? (!!cEntry || refineOpen) : (cReady || !!cEntry)) && composeFocus !== b.id;
    const pLike = it && !provisional && b.kind !== "group" && (!writing || structureLicensed) && paragraphLike(it, { children: kids.length, ready: cReady });
    const canMark = writing && it && !!onMarkThesis && !isEditing && composeFocus !== b.id && !(thesisInPlace && locus.unit_id === b.id);
    const text = [b.text, ...(b.developments || []).map((d) => d.text)].filter(Boolean);
    const fh = formattedHead(b);   // the head paragraph's formatted rendering (with its derived thesis annotation), or null for plain prose
    return (
      <div key={b.id} data-testid={`essay-p-${b.id}`} data-depth={b.depth} data-provenance={b.provenance}
        data-region={b.depth === 0 ? (b.region || "") : undefined} data-region-owner={b.depth === 0 ? regionOwner(b) : undefined}
        className={`group/p relative ${b.depth > 0 ? "ml-6 sm:ml-10 border-l border-stone-200 pl-4" : ""} ${provisional ? "rounded-md border border-dashed border-[#8C3A2A]/35 bg-[#8C3A2A]/[0.025] px-3 py-2" : ""}`}>
        {n && !writing ? <span className="absolute -left-8 top-1 hidden sm:block font-mono text-[10px] text-stone-300 select-none">{n}</span> : null}
        {(b.title || isTitling) ? (
          isTitling ? (
            <span className="flex items-center gap-1.5 mb-1" data-testid={`essay-title-edit-${b.id}`}>
              <input autoFocus data-testid={`essay-title-input-${b.id}`} {...otNoPaste} value={titling.text} maxLength={80}
                onChange={(e) => setTitling({ ...titling, text: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") saveTitle(); if (e.key === "Escape") setTitling(null); }}
                placeholder="What is this paragraph doing? (a few words, yours)"
                className="flex-1 min-w-0 rounded border border-[#8C3A2A]/40 px-2 py-1 text-[14px] font-semibold text-stone-900 outline-none" />
              <button data-testid={`essay-title-save-${b.id}`} onClick={saveTitle} className="text-[11px] font-semibold text-[#8C3A2A]">save</button>
              <button onClick={() => setTitling(null)} className="text-stone-400 hover:text-stone-600"><X className="h-3.5 w-3.5" /></button>
            </span>
          ) : (
            <h3 data-testid={`essay-title-${b.id}`} className={`font-serif text-[17px] leading-snug text-stone-900 mb-1 ${b.depth > 0 ? "text-[15px]" : ""}`}>
              {b.title}
              {b.title_provenance === "adopted_from_tws" && !writing ? <span className="ml-2 font-mono text-[8px] uppercase tracking-[0.14em] text-stone-400">kept from tws</span> : null}
              {it ? (
                <button data-testid={`essay-title-btn-${b.id}`} onClick={() => setTitling({ id: b.id, text: b.title })}
                  className="ml-2 inline-flex items-center gap-0.5 align-middle text-[10px] font-sans font-normal text-stone-400 opacity-0 group-hover/p:opacity-100 focus:opacity-100 hover:text-[#8C3A2A]"><Tag className="h-2.5 w-2.5" /> edit title</button>
              ) : null}
            </h3>
          )
        ) : null}
        {provisional ? (
          <p data-testid={`essay-provisional-${b.id}`} className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-[#8C3A2A]/70 mb-1">
            tws · provisional{b.provenance === "tws_inferred" ? " sketch" : b.provisional_placement && b.provenance === "student" ? " place" : " part"} — decide it in Structure view
          </p>
        ) : null}
        {composeFocus === b.id && it ? (
          <ComposeUnit unit={it} view={composeView} busy={composeBusy}
            onTurn={(text, kind, message) => onComposeTurn && onComposeTurn(b.id, text, kind, message)}
            onReturn={() => onComposeReturn && onComposeReturn(b.id)} />
        ) : isEditing ? editor(b.id, b.text) : (
          <div data-testid={`essay-text-${b.id}`} onClick={() => { if (it && !provisional) openEdit(b.id, [b.text, ...(b.developments || []).map((d) => d.text)].filter(Boolean).join("\n\n"), b.text); }}
            className={`${it && !provisional ? "cursor-text" : ""} rounded-md -mx-1 px-1 transition-colors ${it && !provisional ? "hover:bg-stone-100/70" : ""}`}>
            {text.length === 0 && b.kind === "group" ? (
              <p className="text-[12px] italic text-stone-400">(a part you named — its paragraphs follow)</p>
            ) : text.map((t, k) => {
              // the paragraph holding the marked thesis carries a flag only; the label itself is INLINE, immediately before the thesis span (markedText
              // or the annotated formatting); "status" = formatting kept, no safe inline match, the label stands under the paragraph instead
              const labelHere = thesisInPlace && b.thesis && b.thesis.where === (k === 0 ? "text" : `development:${k - 1}`);
              return (
              <p key={k} data-testid={k === 0 ? `essay-head-${b.id}` : `essay-dev-${b.id}-${k}`} data-thesis-label={labelHere ? (k === 0 && fh && !fh.marked ? "status" : "inline") : undefined}
                className={`text-[15px] leading-[1.75] whitespace-pre-line ${provisional ? "text-stone-600" : "text-stone-900"} ${k > 0 ? "mt-2" : ""}`}>{
                  (k === 0 && fh)
                    ? <span data-testid={`essay-formatted-${b.id}`} className="writing-formatted" data-annotated={fh.marked ? "thesis" : undefined}
                        onClick={onMarkEvent} onKeyDown={onMarkEvent} dangerouslySetInnerHTML={{ __html: fh.html }} />
                    : markedText(b, t, k)}</p>
              );
            })}
            {thesisStatusEl(b, fh)}
          </div>
        )}
        {thesisMenuEl(b)}
        {writing && editing && editing.id === "thesis" && thesisInPlace && locus.unit_id === b.id ? editor("thesis", thesis) : null}
        {pickerEl(b, text)}
        {regionPickEl(b)}
        {!isEditing && composeFocus !== b.id && it && !provisional ? (
          <div className="mt-1 flex flex-wrap items-center gap-2 opacity-0 group-hover/p:opacity-100 focus-within:opacity-100 transition-opacity">
            {b.kind !== "group" ? (
              <button data-testid={`essay-edit-btn-${b.id}`} title={EDIT_TEXT_TIP} onClick={() => openEdit(b.id, text.join("\n\n"), b.text)}
                className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#8C3A2A]"><Pencil className="h-3 w-3" /> Edit text</button>
            ) : null}
            {writing && onSetRegionStart && b.depth === 0 && regionPick !== b.id ? (
              <button data-testid={`essay-region-start-btn-${b.id}`} onClick={() => setRegionPick(b.id)}
                className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#8C3A2A]">Starts a new part…</button>
            ) : null}
            {canMark && picking !== b.id ? (
              <button data-testid={`essay-mark-thesis-${b.id}`} onClick={() => setPicking(b.id)}
                className="inline-flex items-center gap-1 text-[11px] text-stone-500 hover:text-[#8C3A2A]">{thesis ? "My main idea is here instead" : "Mark my main idea"}</button>
            ) : null}
            {canRefine ? (
              <button data-testid={`essay-refine-${b.id}`} title={WORK_WITH_COACH_TIP} onClick={() => onComposeOpen(b.id)}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-800 border border-emerald-400 rounded-full px-2 py-0.5 hover:bg-emerald-50">
                <PenLine className="h-3 w-3" /> {cEntry ? "Keep working with coach" : "Work with coach"}
              </button>
            ) : null}
            {pLike && !b.title ? (
              <button data-testid={`essay-title-invite-${b.id}`} onClick={() => setTitling({ id: b.id, text: "" })}
                className="inline-flex items-center gap-1 text-[11px] text-[#8C3A2A]/80 hover:text-[#8C3A2A]"><Tag className="h-3 w-3" /> Working title — what is this paragraph doing?</button>
            ) : null}
          </div>
        ) : null}
        {kids.length > 0 && b.depth === 0 ? <div className="mt-3 space-y-3">{kids.map((c) => paragraph(c, [], null))}</div> : null}
      </div>
    );
  };

  // THROUGHLINE — the student's own titles in the student's order (never a fabricated title)
  const throughlineRows = () => {
    const rows = [{ key: "thesis", label: thesis || "(no thesis yet)", kind: "thesis" }];
    placed.forEach((g, k) => {
      if (g.root.kind === "group" && !g.root.title) return;
      rows.push({ key: g.root.id, kind: g.root.region === "conclusion" ? "conclusion" : "p", n: k + 1,
        label: g.root.title || openingWords(g.root.text), untitled: !g.root.title, region: g.root.region });
    });
    return rows;
  };

  let n = 0;
  const renderGroup = (g) => { n += 1; return paragraph(g.root, g.kids, n); };
  const regionOf = (g) => (REGION_TITLE[g.root.region] ? g.root.region : "body");   // no stated region → shown in the Body (as the map does)
  const byRegion = (key) => placed.filter((g) => regionOf(g) === key);
  const showThroughline = !writing || (structureLicensed && placed.some((g) => g.root.title));

  // REGION LABELS — one quiet line at each boundary. STUDENT-ESTABLISHED: the ordinary label. TWS-PROPOSED (a provisional placement the
  // student has not owned): tentative, italic, with a quiet correction. NOT YET ESTABLISHED (material shown in the Body's place for
  // document order only): NO label — the prose stays prose; nothing here claims the student asserted "Body".
  const regionLabel = (rk, gs) => {
    if (!writing) return <p data-testid={`essay-region-label-${rk}`} className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-400 pt-1">{REGION_TITLE[rk]}</p>;
    const owners = gs.map((g) => regionOwner(g.root));
    if (owners.includes("student")) return <p data-testid={`essay-region-label-${rk}`} data-owner="student" className="font-mono text-[9px] uppercase tracking-[0.2em] text-stone-400 pt-1">{REGION_TITLE[rk]}</p>;
    if (owners.includes("tws")) {
      const first = gs[owners.indexOf("tws")].root;
      return (
        <p data-testid={`essay-region-label-${rk}`} data-owner="tws" className="pt-1 text-[10.5px] italic text-stone-400">
          TWS reads this as your {REGION_TITLE[rk].toLowerCase()}
          {onSetRegionStart ? <> · <button data-testid={`essay-region-change-${rk}`} onClick={() => setRegionPick(first.id)} className="not-italic underline underline-offset-2 decoration-stone-300 hover:text-[#8C3A2A]">Change</button></> : null}
        </p>
      );
    }
    return null;
  };

  // THE GLOBAL COVERAGE CHECKPOINT, IN ESSAY VIEW — the existing question over the boundaries the student can see here; "Not quite" keeps
  // them in this view to correct the boundaries with the same controls. Structure view is never required.
  const coverageEl = writing && coverage && coverage.due && onCoverage ? (
    <div data-testid="essay-coverage" data-status={coverage.status} className="rounded-md border border-[#8C3A2A]/20 bg-[#8C3A2A]/[0.03] px-4 py-3 space-y-2">
      {coverage.stale ? <p data-testid="essay-coverage-stale" className="text-[11px] text-stone-500 italic">Your whole has changed since you confirmed it.</p> : null}
      <p data-testid="essay-coverage-question" className="text-[13px] leading-snug text-stone-800">Before we work paragraph by paragraph, does this match how you see the major parts of your essay?</p>
      {coverage.status === "disputed" ? <p data-testid="essay-coverage-disputed" className="text-[11.5px] text-stone-600">Change where any part begins below (<span className="italic">Starts a new part…</span>), then answer again.</p> : null}
      {coverage.status === "unsure" ? <p data-testid="essay-coverage-unsure-note" className="text-[11.5px] text-stone-600">Take your time — look at where each part begins, then answer when it feels right.</p> : null}
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        <button data-testid="essay-coverage-yes" onClick={() => onCoverage("yes")} className="rounded-full bg-[#8C3A2A] text-white px-3 py-1">Yes</button>
        <button data-testid="essay-coverage-not-quite" onClick={() => onCoverage("not_quite")} className="rounded-full border border-stone-300 text-stone-700 px-3 py-1 hover:bg-stone-100">Not quite</button>
        <button data-testid="essay-coverage-unsure" onClick={() => onCoverage("unsure")} className="rounded-full border border-stone-300 text-stone-700 px-3 py-1 hover:bg-stone-100">Unsure</button>
      </div>
    </div>
  ) : null;

  // WRITING FRAME: the thesis stands as its own line ONLY when its words are no longer in any paragraph (the student deleted or
  // rewrote the sentence past recognition) — a stated judgment that lost its place, shown as such, never re-extracted
  const unlocatedThesis = writing && thesis && !locus ? (
    <div data-testid="essay-thesis-unlocated" className="border-l-2 border-[#8C3A2A]/40 pl-3">
      <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C3A2A]">your main idea <span className="text-stone-400 normal-case tracking-normal">· no longer in your paragraphs — write it back in, or tap to change it</span></p>
      {editing && editing.id === "thesis" ? editor("thesis", thesis) : (
        <p data-testid="essay-thesis" onClick={() => openEdit("thesis", thesis, thesis)}
          className="text-[15px] leading-[1.75] text-stone-800 cursor-text rounded-md -mx-1 px-1 hover:bg-stone-100/70">{thesis}</p>
      )}
      {onClearThesis ? <button data-testid="essay-thesis-unmark" onClick={() => onClearThesis()} className="text-[11px] text-stone-400 hover:text-[#8C3A2A]">Not my main idea any more</button> : null}
    </div>
  ) : null;

  return (
    <div data-testid="essay-view" data-tour="essay-editor" data-frame={frame} className="space-y-5" {...otOwnCopy}>
      {(!writing || showThroughline) ? (
        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
          {!writing ? <p className="text-[11.5px] text-stone-500 italic">The same writing as your Structure view, read as one piece. Click any paragraph to edit it here.</p> : <span />}
          {showThroughline ? (
            <label className="inline-flex items-center gap-1.5 text-[11.5px] text-stone-600 cursor-pointer select-none">
              <input data-testid="essay-throughline-toggle" type="checkbox" checked={throughline} onChange={(e) => setThroughline(e.target.checked)} className="accent-[#8C3A2A]" />
              Show throughline
            </label>
          ) : null}
        </div>
      ) : null}
      {throughline && showThroughline ? (
        <ol data-testid="essay-throughline" className="rounded-xl border border-[#8C3A2A]/25 bg-[#8C3A2A]/[0.03] px-4 py-3 space-y-1">
          {throughlineRows().map((r, k) => (
            <li key={r.key} data-testid={`essay-tl-${r.key}`} className="flex items-start gap-2 text-[13px] leading-snug">
              {k > 0 ? <span className="font-mono text-[10px] text-[#8C3A2A]/60 mt-0.5">↓</span> : <span className="font-mono text-[10px] text-[#8C3A2A] mt-0.5">●</span>}
              <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-400 mt-0.5 shrink-0 w-16">{r.kind === "thesis" ? "thesis" : r.kind === "conclusion" ? "concl." : `P${r.n}`}</span>
              <span className={`${r.untitled ? "text-stone-600" : "text-stone-800 font-medium"}`}>{r.label}</span>
            </li>
          ))}
        </ol>
      ) : null}

      {writing && canUndo && onUndo ? (
        <div className="flex items-center justify-end px-1">
          <button data-testid="essay-undo" onClick={onUndo} className="text-[11px] font-medium text-[#8C3A2A] border border-[#8C3A2A]/30 rounded-full px-2.5 py-1 hover:bg-[#8C3A2A]/5">Undo last move</button>
        </div>
      ) : null}

      <article data-testid="essay-body" className={`rounded-xl border bg-white px-5 py-6 sm:px-10 sm:py-8 space-y-4 max-w-[760px] ${writing ? "border-stone-200/80" : "border-stone-200"}`}>
        {coverageEl}
        {unlocatedThesis}
        {(regionsActive ? ["introduction", "body", "conclusion"] : ["body"]).map((rk) => {
          const gs = regionsActive ? byRegion(rk) : placed;
          const showThesis = !writing && ((regionsActive ? ((doc.thesis && doc.thesis.region) || "introduction").toLowerCase() : "body") === rk);
          if (!showThesis && gs.length === 0) return null;
          return (
            <section key={rk} data-testid={`essay-region-${rk}`} className="space-y-4">
              {regionsActive ? regionLabel(rk, gs) : null}
              {showThesis ? (
                editing && editing.id === "thesis" ? editor("thesis", thesis) : (
                  <p data-testid="essay-thesis" data-provisional={provisionalThesis ? "true" : undefined}
                    onClick={() => openEdit("thesis", thesis || provisionalThesis, thesis)}
                    title={provisionalThesis ? "How The Writing Studio reads your answer so far, in your words — tap to keep or change it" : undefined}
                    className={`text-[15px] leading-[1.75] cursor-text rounded-md -mx-1 px-1 hover:bg-stone-100/70 ${thesis ? "text-stone-900 font-medium" : provisionalThesis ? "text-stone-700 italic border-l-2 border-dashed border-[#8C3A2A]/40 pl-2.5" : "text-stone-400 italic"}`}>
                    {thesis || provisionalThesis || "Your main idea has not been put into words yet — it can grow out of your paragraphs."}
                  </p>
                )
              ) : null}
              {gs.map(renderGroup)}
            </section>
          );
        })}
        {placed.length === 0 && !thesis ? (
          <p data-testid="essay-empty" className="text-[13px] italic text-stone-400">
            {writing ? "Your essay will take shape here as you write." : "Nothing placed in the essay yet — what you develop in Structure view appears here as paragraphs."}
          </p>
        ) : null}
        {writing && onAddParagraph ? (
          <div data-testid="essay-continue" className="pt-1">
            {adding ? (
              <div>
                <WritingEditor testid="essay-add-input" initialText={addText} initialFormatting={addFormatting} onChange={changeAdd} onSave={saveAdd} onCancel={cancelAdd}
                  placeholder="The next paragraph, in your words\u2026" />
                <div className="flex items-center justify-end gap-2 mt-1.5">
                  <span className="mr-auto text-[10.5px] text-stone-400">Kept as you write \u00b7 Add puts it in your essay \u00b7 \u2318/Ctrl+Return adds now</span>
                  <button data-testid="essay-add-cancel" onClick={cancelAdd} className="text-[11.5px] text-stone-500 px-2">Cancel</button>
                  <button data-testid="essay-add-save" data-tour="essay-add-save" onClick={saveAdd} disabled={!addText.trim()} className="inline-flex items-center gap-1 bg-[#8C3A2A] text-white text-[11.5px] px-3 py-1 rounded disabled:opacity-40"><Check className="h-3 w-3" /> Add</button>
                </div>
              </div>
            ) : (
              <button data-testid="essay-add-paragraph" data-tour="essay-write-next" onClick={() => setAdding(true)}
                className="inline-flex items-center gap-1 text-[12px] text-stone-400 hover:text-[#8C3A2A]"><Plus className="h-3.5 w-3.5" /> Write the next paragraph</button>
            )}
          </div>
        ) : null}
      </article>

      {developing.length > 0 ? (
        <section data-testid="essay-developing" className="rounded-xl border border-dashed border-stone-300 bg-white/60 px-5 py-4 space-y-3 max-w-[760px]">
          <p className="font-serif text-[14px] text-stone-700">Thoughts I’m still developing <span className="text-[11px] text-stone-500 italic">— {writing ? "something you said in the conversation — not part of your essay until you place it or write it in" : "not part of the essay until you place them"}</span></p>
          {developing.map((g) => paragraph(g.root, g.kids, null))}
        </section>
      ) : null}
    </div>
  );
}
